/**
 * Floating "Z 交谈 / 查看 / 进入" label at the bottom centre, driven by bus interact:prompt
 * ({ label?, kind?, id? } | null). The verb defaults from `kind` when no label is given.
 * Hidden automatically while dialogue or a modal is showing.
 */
import Phaser from 'phaser';
import { Events } from '@/core/events';
import { GAME_W } from '@/core/config';
import type { UiRoot } from './UiRoot';
import { uiText } from './text';
import { Css, Layout, Slices, UiDepth } from './theme';
import { promptLabel, type InteractPromptPayload } from './promptLabel';

export class InteractPrompt {
  readonly container: Phaser.GameObjects.Container;
  private readonly bar: Phaser.GameObjects.NineSlice;
  private readonly keyCap: Phaser.GameObjects.NineSlice;
  private readonly keyText: Phaser.GameObjects.Text;
  private readonly label: Phaser.GameObjects.Text;
  private wanted = false;
  private shown = false;
  private currentLabel = '';
  private current: NonNullable<InteractPromptPayload> | null = null;

  constructor(private readonly root: UiRoot, private readonly suppressed: () => boolean) {
    const s = root.scene;
    this.container = s.add.container(GAME_W / 2, Layout.dialogue.y - 16).setDepth(UiDepth.PROMPT).setVisible(false).setAlpha(0);
    this.bar = s.add.nineslice(0, 0, Slices.barDim.key, undefined, 100, 24, Slices.barDim.l, Slices.barDim.r, Slices.barDim.t, Slices.barDim.b).setOrigin(0.5);
    this.keyCap = s.add.nineslice(0, 0, Slices.bar.key, undefined, 18, 18, Slices.bar.l, Slices.bar.r, Slices.bar.t, Slices.bar.b).setOrigin(0.5);
    this.keyText = uiText(s, 0, 0, 'Z', { fontSize: '11px', fontStyle: 'bold', color: Css.parchment }).setOrigin(0.5);
    this.label = uiText(s, 0, 0, '', { fontSize: '13px', color: Css.parchment }).setOrigin(0, 0.5);
    this.container.add([this.bar, this.keyCap, this.keyText, this.label]);
    root.onBus(Events.INTERACT_PROMPT, (p) => { if (p) this.showPayload(p); else this.hide(); });
    root.onUpdate(() => this.refresh());
    root.track(() => this.container.destroy(true));
  }

  /** Shows the prompt for a bus payload (label resolved from kind when absent). */
  showPayload(p: NonNullable<InteractPromptPayload>): void {
    this.current = p;
    this.show(promptLabel(p));
  }

  show(label: string): void {
    this.wanted = true;
    if (label !== this.currentLabel) {
      this.currentLabel = label;
      this.label.setText(label);
      this.relayout();
    }
    this.refresh();
  }

  hide(): void { this.wanted = false; this.current = null; this.refresh(); }

  get isShown(): boolean { return this.shown; }
  get text(): string { return this.currentLabel; }
  /** The payload currently shown (null when hidden). */
  get payload(): NonNullable<InteractPromptPayload> | null { return this.wanted ? this.current : null; }

  /** Uses a key cap glyph other than Z (e.g. "点" on touch). */
  setKeyGlyph(glyph: string): void { this.keyText.setText(glyph); this.relayout(); }

  private relayout(): void {
    const capW = Math.max(18, Math.round(this.keyText.width) + 8);
    const w = Math.round(this.label.width) + capW + 26;
    this.bar.setSize(w, 24);
    this.keyCap.setSize(capW, 18);
    this.keyCap.setPosition(-w / 2 + 5 + capW / 2, 0);
    this.keyText.setPosition(-w / 2 + 5 + capW / 2, 0);
    this.label.setPosition(-w / 2 + capW + 10, 0);
  }

  private refresh(): void {
    const want = this.wanted && !this.suppressed();
    if (want === this.shown) return;
    this.shown = want;
    const s = this.root.scene;
    s.tweens.killTweensOf(this.container);
    if (want) {
      this.container.setVisible(true);
      s.tweens.add({ targets: this.container, alpha: 1, duration: this.root.reduceMotion() ? 0 : 100 });
    } else {
      s.tweens.add({ targets: this.container, alpha: 0, duration: this.root.reduceMotion() ? 0 : 100, onComplete: () => { if (!this.shown) this.container.setVisible(false); } });
    }
  }
}
